'use client';

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["component", "colorSet", "buttonSize", "size", "fullWidth", "iconLeading", "iconTrailing", "iconOnly", "children", "className", "UNSAFE_colorSet", "aria-label", "aria-labelledby", "aria-hidden"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext } from 'react';
import { BrowserDefaultFocusStyleContext } from "../../contexts/BrowserDefaultFocusStyleContext.mjs";
import { EncoreContext, encoreContextKeyword, getStatus } from "../../contexts/EncoreContext.mjs";
import { KeyboardDetectionContext } from "../../contexts/KeyboardDetectionContext.mjs";
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { addColorSet, buttonSizesWithDeprecationWarning, defaultTheme, getButtonFont, getNextControlSize, textClass } from "../../styles/index.mjs";
import { Button } from "./Button.mjs";
import { ButtonChildren } from "./ButtonChildren.mjs";
import { ButtonFocus } from "./ButtonFocus.mjs";
import { ButtonInner } from "./ButtonInner.mjs";
import { warn } from "../../logging.mjs";
var ButtonComponent = /*#__PURE__*/React.forwardRef(function ButtonComponent(_ref, ref) {
  var component = _ref.component,
    _ref$colorSet = _ref.colorSet,
    colorSet = _ref$colorSet === void 0 ? 'brightAccent' : _ref$colorSet,
    buttonSize = _ref.buttonSize,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    fullWidth = _ref.fullWidth,
    iconLeading = _ref.iconLeading,
    iconTrailing = _ref.iconTrailing,
    iconOnly = _ref.iconOnly,
    children = _ref.children,
    className = _ref.className,
    UNSAFE_colorSet = _ref.UNSAFE_colorSet,
    ariaLabel = _ref['aria-label'],
    ariaLabelledby = _ref['aria-labelledby'],
    ariaHidden = _ref['aria-hidden'],
    props = _objectWithoutProperties(_ref, _excluded);
  if (iconOnly && !ariaLabel && !ariaLabelledby && !ariaHidden) {
    warn('ButtonPrimary must have an aria-label or aria-labelledby attribute defined when using the iconOnly prop in order to be accessible.');
  }
  var _useContext = useContext(KeyboardDetectionContext),
    isUsingKeyboard = _useContext.isUsingKeyboard;
  var _useContext2 = useContext(BrowserDefaultFocusStyleContext),
    useBrowserDefaultFocusStyle = _useContext2.useBrowserDefaultFocusStyle;
  var config = useContext(EncoreContext);
  var status = getStatus(encoreContextKeyword.button, config);
  var normalizedSize = buttonSize ? getNextControlSize(buttonSize) : size;
  var rel = props.href && props.target === '_blank' ? 'noopener noreferrer' : null;
  var fallbackSet = defaultTheme[colorSet];
  return /*#__PURE__*/React.createElement(Button, Object.assign({}, props, {
    rel: rel,
    ref: ref,
    component: !component && props.href ? 'a' : component,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-hidden": ariaHidden,
    fullWidth: fullWidth,
    fallbackSet: fallbackSet,
    useBrowserDefaultFocusStyle: useBrowserDefaultFocusStyle,
    colorSet: colorSet,
    UNSAFE_colorSet: UNSAFE_colorSet,
    "data-encore-id": ENCORE_DATA_IDS.ButtonPrimary,
    "data-is-icon-only": iconOnly ? true : undefined,
    className: !iconOnly && textClass(getButtonFont(normalizedSize, status))
  }), /*#__PURE__*/React.createElement(ButtonInner, {
    className: UNSAFE_colorSet ? className : addColorSet(colorSet, className),
    $iconLeading: iconLeading,
    $iconTrailing: iconTrailing,
    $iconOnly: iconOnly,
    $UNSAFE_colorSet: UNSAFE_colorSet,
    $buttonSize: normalizedSize,
    $fallbackSet: fallbackSet,
    $status: status
  }, /*#__PURE__*/React.createElement(ButtonChildren, {
    iconOnly: iconOnly,
    iconLeading: iconLeading,
    iconTrailing: iconTrailing,
    buttonSize: buttonSize ? getNextControlSize(buttonSize) : size,
    children: children
  })), !useBrowserDefaultFocusStyle && /*#__PURE__*/React.createElement(ButtonFocus, {
    $isUsingKeyboard: isUsingKeyboard
  }));
});

/**
 * TODO: Remove when we deprecate `buttonSize` helpers like `ButtonPrimary.sm`.
 */
var buttonHelpers = _objectSpread(_objectSpread({}, buttonSizesWithDeprecationWarning), {}, {
  displayName: 'ButtonPrimary'
});

/**
 * Primary buttons are for the action with the highest priority. They have filled backgrounds and use the bright accent color set by default.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-buttonprimary--docs) |
 * [Encore site](https://encore.spotify.net/web/components/buttons#primary) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/ButtonPrimary) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32403-19607&t=B794x4x2a3huupUd-1)
 */
export var ButtonPrimary = Object.assign(ButtonComponent, buttonHelpers);